<template>
  <VApp>
    <AdminLayout v-if="isAdmin" />
    <MainLayout v-else />

    <SnackbarQueue class="pb-6" />

    <VFooter color="white" class="p-4 pl-6 pr-6 flex-none">
      <PageFooter />
    </VFooter>
  </VApp>
</template>

<script setup lang="ts">
import MainLayout from '@/layouts/MainLayout.vue'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import SnackbarQueue from '@/components/notices/SnackbarQueue.vue'
import PageFooter from '@/components/page/PageFooter.vue'
import { onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue'
import { UserSettingsService } from './services/telehealthApi/UserSettingsService'
import { AuthenticationService } from './services/AuthenticationService'
import { getRefreshIntervalCheckLoginStatus } from './global/environmentGetters'
import { useTimeStore } from './stores/time'
import useUserStateHandler from './composables/useUserStateHandler'
import { usePushNotificationListener } from '@/composables/usePushNotificationListener'

const { user } = storeToRefs(useUserStore())
const { startCurrentTimeUpdate, stopCurrentTimeUpdate } = useTimeStore()
const { isAdmin, hadSelectedShift } = useUserStateHandler()

const checkShiftInterval = ref<ReturnType<typeof setInterval> | null>(null)

usePushNotificationListener()

onMounted(() => {
  startCurrentTimeUpdate()
  window.addEventListener('dragover', handleWindowDragOver)
})

onBeforeUnmount(() => {
  if (checkShiftInterval.value) {
    clearInterval(checkShiftInterval.value)
    checkShiftInterval.value = null
  }
  stopCurrentTimeUpdate()
})

onUnmounted(() => {
  window.removeEventListener('dragover', handleWindowDragOver)
})

function handleWindowDragOver(event: DragEvent) {
  event.preventDefault()
}

watch(
  () => hadSelectedShift.value,
  (newValue) => {
    if (newValue) {
      checkShiftInterval.value = setInterval(async () => {
        const response = await UserSettingsService.getUserSettings(user.value!.userName)

        if (!response.userSettings.shift?.type) {
          await AuthenticationService.logout()
        }
      }, getRefreshIntervalCheckLoginStatus())
    }
  },
  { immediate: true }
)
</script>

<style>
html {
  overflow-y: auto !important;
}
</style>
